import { Action } from "@packages/shared-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { GetPnLReport200 } from "@/api/financial";
import {
	exportFinancialTransactionsQueryOptions,
	getCashFlowReportQueryOptions,
	getFinancialTimelineQueryOptions,
	getPnLReportQueryOptions,
} from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatMoney } from "@/utils/financial";
import { ComparisonSelector } from "../-components/ComparisonSelector";
import { CostCentersReport } from "../-components/CostCentersReport";
import { keepScope } from "../-components/farmScopeSearch";
import { KpisReport } from "../-components/KpisReport";
import { PeriodSelector } from "../-components/PeriodSelector";
import { ProductionResultReport } from "../-components/ProductionResultReport";
import { ProjectedTimelineChart } from "../-components/ProjectedTimelineChart";
import {
	type ComparisonMode,
	getComparisonRange,
	getPeriodRange,
	type PeriodPreset,
} from "../-components/period";

const TIMELINE_MONTHS_AHEAD = "6";

const REPORT_TABS = [
	"pnl",
	"cashFlow",
	"timeline",
	"costCenters",
	"kpis",
	"productionResult",
] as const;
type ReportTab = (typeof REPORT_TABS)[number];

// `tab` in the URL makes report tabs deep-linkable (the overview KPI cards
// land on a specific tab). `farm` is the inherited farm-scope param.
const searchSchema = z.object({
	tab: z.enum(REPORT_TABS).optional(),
	farm: z.string().optional(),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/reports/",
)({
	validateSearch: searchSchema,
	component: FinancialReportsPage,
});

function FinancialReportsPage() {
	const { orgId } = Route.useParams();
	const { tab } = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const { t } = useTranslation();
	const scope = useFarmScope();
	const queryClient = useQueryClient();
	const canExport = useHasPermission(orgId)(Action.EXPORT_FINANCE);
	const [period, setPeriod] = useState<PeriodPreset>("thisMonth");
	const [comparison, setComparison] = useState<ComparisonMode>("none");
	const [cashFlowBasis, setCashFlowBasis] = useState<"event" | "cash">("event");
	const [isExporting, setIsExporting] = useState(false);

	const range = getPeriodRange(period);
	const compareRange = getComparisonRange(range, comparison, period);
	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;
	const reportParams = {
		periodStart: range.periodStart,
		periodEnd: range.periodEnd,
		...(establishmentId ? { establishmentId } : {}),
	};

	const { data: pnl, isLoading: pnlLoading } = useQuery(
		getPnLReportQueryOptions(orgId, {
			...reportParams,
			...(compareRange
				? {
						comparePeriodStart: compareRange.periodStart,
						comparePeriodEnd: compareRange.periodEnd,
					}
				: {}),
		}),
	);
	const { data: cashFlow, isLoading: cashFlowLoading } = useQuery(
		getCashFlowReportQueryOptions(orgId, {
			...reportParams,
			basis: cashFlowBasis,
		}),
	);
	// The projected timeline is forward-looking (a fixed horizon), so it uses
	// `monthsAhead` rather than the period selector that scopes P&L / cash-flow.
	const { data: timeline, isLoading: timelineLoading } = useQuery(
		getFinancialTimelineQueryOptions(orgId, {
			monthsAhead: TIMELINE_MONTHS_AHEAD,
			...(establishmentId ? { establishmentId } : {}),
		}),
	);

	const { formatDateOnly } = useDateFormatter();

	const handleExport = async () => {
		setIsExporting(true);
		try {
			const csv = await queryClient.fetchQuery(
				exportFinancialTransactionsQueryOptions(orgId, {
					periodStart: range.periodStart,
					periodEnd: range.periodEnd,
					...(establishmentId ? { establishmentId } : {}),
				}),
			);
			const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement("a");
			anchor.href = url;
			anchor.download = `${t("financial.reports.export.filename")}.csv`;
			anchor.click();
			URL.revokeObjectURL(url);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div className="flex flex-col gap-6">
			{/* Export is parameterized by the two selectors, so it stays in the same
			    control cluster rather than moving to the section header with the
			    page CTAs. The selectors sit side by side at every width. */}
			<div className="flex flex-wrap items-center gap-2 sm:justify-end">
				<div className="flex min-w-0 flex-1 basis-full items-center gap-2 sm:basis-auto">
					<PeriodSelector value={period} onChange={setPeriod} />
					<ComparisonSelector value={comparison} onChange={setComparison} />
				</div>
				{canExport && (
					<Button
						variant="outline"
						className="relative ml-auto h-8 shrink-0 px-2.5 text-xs after:absolute after:inset-x-0 after:-inset-y-1.5 sm:ml-0 sm:h-9 sm:px-4 sm:text-sm sm:after:hidden"
						onClick={handleExport}
						disabled={isExporting}
					>
						<Download className="mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />
						<span className="sm:hidden">
							{t("financial.reports.export.buttonShort")}
						</span>
						<span className="hidden sm:inline">
							{t("financial.reports.export.button")}
						</span>
					</Button>
				)}
			</div>

			<Tabs
				value={tab ?? "pnl"}
				onValueChange={(value) =>
					navigate({
						search: (prev) => ({ ...prev, tab: value as ReportTab }),
						replace: true,
					})
				}
			>
				{/* The 6 report tabs are wider than a phone viewport; wrap the
				    inline-flex TabsList in a hidden-scrollbar strip so the tail
				    tabs stay reachable by swipe instead of overflowing the page. */}
				<div className="-mx-1 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
					<TabsList>
						<TabsTrigger value="pnl">
							{t("financial.reports.tabs.pnl")}
						</TabsTrigger>
						<TabsTrigger value="cashFlow">
							{t("financial.reports.tabs.cashFlow")}
						</TabsTrigger>
						<TabsTrigger value="timeline">
							{t("financial.reports.tabs.timeline")}
						</TabsTrigger>
						<TabsTrigger value="costCenters">
							{t("financial.reports.tabs.costCenters")}
						</TabsTrigger>
						<TabsTrigger value="kpis">
							{t("financial.reports.tabs.kpis")}
						</TabsTrigger>
						<TabsTrigger value="productionResult">
							{t("financial.reports.tabs.productionResult")}
						</TabsTrigger>
					</TabsList>
				</div>

				<TabsContent value="pnl">
					{pnlLoading ? (
						<div className="flex justify-center py-12">
							<LoadingSpinner />
						</div>
					) : pnl ? (
						<PnLStatement orgId={orgId} pnl={pnl} />
					) : null}
				</TabsContent>

				<TabsContent value="cashFlow" className="flex flex-col gap-3">
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<div className="inline-flex rounded-md border border-base-300 p-0.5">
							{(["event", "cash"] as const).map((b) => (
								<button
									type="button"
									key={b}
									onClick={() => setCashFlowBasis(b)}
									className={
										cashFlowBasis === b
											? "rounded px-3 py-1 text-sm font-medium bg-primary text-primary-content"
											: "rounded px-3 py-1 text-sm font-medium text-base-content/70 hover:bg-base-200"
									}
								>
									{t(`financial.reports.cashFlow.basis.${b}`)}
								</button>
							))}
						</div>
					</div>
					{cashFlowBasis === "cash" && (
						<p className="text-xs text-base-content/60">
							{t("financial.reports.cashFlow.basis.cashCaveat")}
						</p>
					)}
					{cashFlowLoading ? (
						<div className="flex justify-center py-12">
							<LoadingSpinner />
						</div>
					) : cashFlow && cashFlow.buckets.length > 0 ? (
						<Card>
							<CardContent className="p-0">
								{/* Four money columns exceed a phone width; let the
								    table scroll horizontally inside the card instead of
								    clipping the tail columns (Money out / Net). */}
								<div className="overflow-x-auto">
									<table className="w-full min-w-[32rem] text-sm">
										<thead>
											<tr className="border-b border-base-200 text-left text-base-content/60">
												<th className="p-3">
													{t("financial.reports.cashFlow.period")}
												</th>
												<th className="p-3 text-right">
													{t("financial.reports.cashFlow.moneyIn")}
												</th>
												<th className="p-3 text-right">
													{t("financial.reports.cashFlow.moneyOut")}
												</th>
												<th className="p-3 text-right">
													{t("financial.reports.cashFlow.net")}
												</th>
											</tr>
										</thead>
										<tbody>
											{cashFlow.buckets.map((bucket) => (
												<tr
													key={bucket.periodStart}
													className="border-b border-base-200 last:border-0"
												>
													<td className="p-3">
														{formatDateOnly(
															new Date(bucket.periodStart).getTime(),
														)}
													</td>
													<td className="p-3 text-right text-success">
														{formatMoney(bucket.moneyIn, cashFlow.currency)}
													</td>
													<td className="p-3 text-right text-error">
														{formatMoney(bucket.moneyOut, cashFlow.currency)}
													</td>
													<td className="p-3 text-right font-medium">
														{formatMoney(bucket.net, cashFlow.currency)}
													</td>
												</tr>
											))}
										</tbody>
										<tfoot>
											<tr className="font-semibold">
												<td className="p-3">{t("financial.overview.net")}</td>
												<td className="p-3 text-right text-success">
													{formatMoney(
														cashFlow.moneyInTotal,
														cashFlow.currency,
													)}
												</td>
												<td className="p-3 text-right text-error">
													{formatMoney(
														cashFlow.moneyOutTotal,
														cashFlow.currency,
													)}
												</td>
												<td className="p-3 text-right">
													{formatMoney(cashFlow.netTotal, cashFlow.currency)}
												</td>
											</tr>
										</tfoot>
									</table>
								</div>
							</CardContent>
						</Card>
					) : (
						<p className="py-12 text-center text-sm text-base-content/60">
							{t("financial.reports.cashFlow.empty")}
						</p>
					)}
				</TabsContent>

				<TabsContent value="timeline">
					{timelineLoading ? (
						<div className="flex justify-center py-12">
							<LoadingSpinner />
						</div>
					) : timeline && timeline.buckets.length > 0 ? (
						<Card>
							<CardHeader>
								<CardTitle className="text-base">
									{t("financial.timeline.title")}
								</CardTitle>
								<p className="text-sm text-base-content/70">
									{t("financial.timeline.subtitle")}
								</p>
								{!timeline.hasAnchor && (
									<p className="text-sm text-warning">
										{t("financial.timeline.noAnchorHint")}{" "}
										<Link
											to="/app/org/$orgId/settings/financial"
											params={{ orgId }}
											className="font-medium underline"
										>
											{t("financial.timeline.noAnchorCta")}
										</Link>
									</p>
								)}
							</CardHeader>
							<CardContent>
								<ProjectedTimelineChart
									buckets={timeline.buckets}
									currency={timeline.currency}
									hasAnchor={timeline.hasAnchor}
								/>
							</CardContent>
						</Card>
					) : (
						<p className="py-12 text-center text-sm text-base-content/60">
							{t("financial.timeline.empty")}
						</p>
					)}
				</TabsContent>

				<TabsContent value="costCenters">
					<CostCentersReport
						orgId={orgId}
						periodStart={range.periodStart}
						periodEnd={range.periodEnd}
						establishmentId={establishmentId}
					/>
				</TabsContent>

				<TabsContent value="kpis">
					<KpisReport
						orgId={orgId}
						periodStart={range.periodStart}
						periodEnd={range.periodEnd}
						establishmentId={establishmentId}
					/>
				</TabsContent>

				<TabsContent value="productionResult">
					<ProductionResultReport
						orgId={orgId}
						periodStart={range.periodStart}
						periodEnd={range.periodEnd}
						establishmentId={establishmentId}
					/>
				</TabsContent>
			</Tabs>
		</div>
	);
}

function PnLStatement({ orgId, pnl }: { orgId: string; pnl: GetPnLReport200 }) {
	const { t } = useTranslation();
	const currency = pnl.currency;
	const comparison = pnl.comparison;

	const linesByKind = (kind: GetPnLReport200["lines"][number]["kind"]) =>
		pnl.lines.filter((line) => line.kind === kind);

	if (pnl.lines.length === 0) {
		return (
			<p className="py-12 text-center text-sm text-base-content/60">
				{t("financial.reports.pnl.empty")}
			</p>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">
					{t("financial.reports.pnl.title")}
				</CardTitle>
				{pnl.unreviewedCount > 0 && (
					<p className="text-sm text-warning">
						{t("financial.reports.pnl.unreviewedHint", {
							count: pnl.unreviewedCount,
						})}
					</p>
				)}
			</CardHeader>
			<CardContent>
				{/* Comparison mode adds three fixed numeric columns (current /
				    prior / delta) that exceed a phone width. Let that mode scroll
				    horizontally inside the card instead of overflowing the page;
				    without a comparison the wrappers collapse (display:contents)
				    so the single-column statement keeps its full-width rows. */}
				<div className={comparison ? "overflow-x-auto" : "contents"}>
					<div
						className={
							comparison ? "w-max min-w-full space-y-1" : "contents space-y-1"
						}
					>
						{comparison && (
							<div className="flex items-center justify-end gap-6 border-b border-base-200 pb-1 text-xs font-semibold uppercase text-base-content/50">
								<span className="w-32 text-right">
									{t("financial.reports.pnl.current")}
								</span>
								<span className="w-32 text-right">
									{t("financial.reports.pnl.comparison")}
								</span>
								<span className="w-24 text-right">
									{t("financial.reports.pnl.delta")}
								</span>
							</div>
						)}
						<KindSection
							orgId={orgId}
							label={t("financial.reports.pnl.revenue")}
							kind="REVENUE"
							lines={linesByKind("REVENUE")}
							currency={currency}
							compact={!!comparison}
						/>
						{comparison && (
							<TotalRow
								label={t("financial.reports.pnl.revenue")}
								value={formatMoney(pnl.revenueTotal, currency)}
								currentAmount={pnl.revenueTotal}
								compareValue={comparison.revenueTotal}
								currency={currency}
							/>
						)}
						<KindSection
							orgId={orgId}
							label={t("financial.reports.pnl.directCost")}
							kind="DIRECT_COST"
							lines={linesByKind("DIRECT_COST")}
							currency={currency}
							compact={!!comparison}
						/>
						{comparison && (
							<TotalRow
								label={t("financial.reports.pnl.directCost")}
								value={formatMoney(pnl.directCostTotal, currency)}
								currentAmount={pnl.directCostTotal}
								compareValue={comparison.directCostTotal}
								currency={currency}
							/>
						)}
						<TotalRow
							label={t("financial.reports.pnl.grossMargin")}
							value={formatMoney(pnl.grossMargin, currency)}
							currentAmount={pnl.grossMargin}
							compareValue={comparison?.grossMargin}
							currency={currency}
							strong
						/>
						<KindSection
							orgId={orgId}
							label={t("financial.reports.pnl.overhead")}
							kind="OVERHEAD"
							lines={linesByKind("OVERHEAD")}
							currency={currency}
							compact={!!comparison}
						/>
						<TotalRow
							label={t("financial.reports.pnl.operatingResult")}
							value={formatMoney(pnl.operatingResult, currency)}
							currentAmount={pnl.operatingResult}
							compareValue={comparison?.operatingResult}
							currency={currency}
							strong
						/>
						<div className="pt-4">
							<TotalRow
								label={t("financial.reports.pnl.capital")}
								value={formatMoney(pnl.capitalTotal, currency)}
								currentAmount={pnl.capitalTotal}
								compareValue={comparison?.capitalTotal}
								currency={currency}
							/>
							<TotalRow
								label={t("financial.reports.pnl.financing")}
								value={formatMoney(pnl.financingTotal, currency)}
								currentAmount={pnl.financingTotal}
								compareValue={comparison?.financingTotal}
								currency={currency}
							/>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function KindSection({
	orgId,
	label,
	kind,
	lines,
	currency,
	compact,
}: {
	orgId: string;
	label: string;
	kind: GetPnLReport200["lines"][number]["kind"];
	lines: GetPnLReport200["lines"];
	currency: string;
	/** When a comparison column is shown, keep line amounts under the "current" column. */
	compact?: boolean;
}) {
	if (lines.length === 0) {
		return null;
	}
	return (
		<div className="py-2">
			<p className="mb-1 text-xs font-semibold uppercase text-base-content/50">
				{label}
			</p>
			<ul>
				{lines.map((line) => (
					<li key={line.categoryId}>
						<Link
							to="/app/org/$orgId/financial/transactions"
							params={{ orgId }}
							search={(prev) => ({
								...keepScope(prev),
								kind,
								categoryId: line.categoryId,
							})}
							className="flex items-center justify-between gap-3 rounded px-1 py-1 text-sm hover:bg-base-200"
						>
							<span className="truncate">{line.categoryLabel}</span>
							<span
								className={
									compact
										? "w-32 shrink-0 text-right font-medium"
										: "shrink-0 font-medium"
								}
							>
								{formatMoney(line.amount, currency)}
							</span>
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}

/** Signed money delta between the current value and its comparison. */
function formatDelta(
	current: string,
	compare: string,
	currency: string,
): { text: string; positive: boolean } {
	const delta = Number(current) - Number(compare);
	const sign = delta > 0 ? "+" : "";
	return {
		text: `${sign}${formatMoney(delta, currency)}`,
		positive: delta >= 0,
	};
}

function TotalRow({
	label,
	value,
	currentAmount,
	compareValue,
	currency,
	strong,
}: {
	label: string;
	value: string;
	/** Raw current amount string (needed to compute the delta vs the comparison). */
	currentAmount?: string;
	/** The comparison-period figure (raw amount string); omitted → no comparison. */
	compareValue?: string;
	/** Required when `compareValue` is supplied, to compute + format the delta. */
	currency?: string;
	strong?: boolean;
}) {
	const rowClass = strong
		? "flex items-center justify-between border-t border-base-300 py-2 font-semibold"
		: "flex items-center justify-between py-1 text-sm";

	if (
		compareValue === undefined ||
		currency === undefined ||
		currentAmount === undefined
	) {
		return (
			<div className={rowClass}>
				<span>{label}</span>
				<span>{value}</span>
			</div>
		);
	}

	const delta = formatDelta(currentAmount, compareValue, currency);
	return (
		<div className={rowClass}>
			<span className="flex-1">{label}</span>
			<span className="w-32 text-right">{value}</span>
			<span className="w-32 text-right text-base-content/60">
				{formatMoney(compareValue, currency)}
			</span>
			<span
				className={
					delta.positive
						? "w-24 text-right text-success"
						: "w-24 text-right text-error"
				}
			>
				{delta.text}
			</span>
		</div>
	);
}
